// web/src/utils/subscriptionStatus.ts
// Shared helpers for showing "subscription/trial expired" visibility across admin/superuser
// views (project pages, schedules, clients list, admin subscriptions). Mirrors the backend's
// scan-blocking logic (pkg/services/scan_scheduler.go isClientSubscriptionActive +
// pkg/api/subscription_enforcement.go IsClientSubscriptionActive): a client's scheduled/manual
// scans are blocked when the subscription is expired, suspended, canceled, or a trial whose
// trial_end has passed. active / past_due (grace) / valid trial are NOT blocked.

import { useCallback, useEffect, useState } from 'react';
import { subscriptionAPI, ClientSubscription } from '../services/api/subscriptions';

export type SubBlockReason =
  | 'subscription_expired'
  | 'trial_expired'
  | 'subscription_suspended'
  | 'subscription_canceled'
  | '';

export interface SubBlockInfo {
  blocked: boolean;        // true => the client's scans are currently blocked by the scheduler
  reason: SubBlockReason;
  status?: string;         // raw subscription status
  trialEnd?: string;       // trial_end ISO string (when relevant)
  label: string;           // short badge label, e.g. "Trial expired"
}

// getSubscriptionBlockInfo computes whether a client's subscription currently blocks scans.
export function getSubscriptionBlockInfo(sub?: ClientSubscription | null): SubBlockInfo {
  if (!sub) {
    return { blocked: false, reason: '', label: '' };
  }
  const now = Date.now();
  switch (sub.status) {
    case 'active':
    case 'past_due':
    case 'paused':
      return { blocked: false, reason: '', status: sub.status, label: '' };
    case 'trialing': {
      const end = sub.trial_end ? new Date(sub.trial_end).getTime() : 0;
      if (end && now > end) {
        return { blocked: true, reason: 'trial_expired', status: sub.status, trialEnd: sub.trial_end, label: 'Trial expired' };
      }
      return { blocked: false, reason: '', status: sub.status, trialEnd: sub.trial_end, label: '' };
    }
    case 'expired':
      return { blocked: true, reason: 'subscription_expired', status: sub.status, label: 'Subscription expired' };
    case 'suspended':
      return { blocked: true, reason: 'subscription_suspended', status: sub.status, label: 'Subscription suspended' };
    case 'canceled':
      return { blocked: true, reason: 'subscription_canceled', status: sub.status, label: 'Subscription canceled' };
    default:
      return { blocked: false, reason: '', status: sub.status, label: '' };
  }
}

// Human-readable title/message for a blocked subscription, phrased for an admin/superuser who is
// viewing a CLIENT's data (third person), not the client's own first-person banner.
export function subscriptionBlockText(info: SubBlockInfo, clientName?: string): { title: string; message: string } {
  const who = clientName ? `${clientName}'s` : "This client's";
  switch (info.reason) {
    case 'trial_expired':
      return {
        title: 'Trial expired — scans paused',
        message: `${who} trial has expired${info.trialEnd ? ` (ended ${new Date(info.trialEnd).toLocaleDateString()})` : ''}. Scheduled scans are paused and will resume automatically once the subscription is renewed.`,
      };
    case 'subscription_expired':
      return {
        title: 'Subscription expired — scans paused',
        message: `${who} subscription has expired. Scheduled scans are paused and will resume automatically once renewed.`,
      };
    case 'subscription_suspended':
      return {
        title: 'Subscription suspended — scans paused',
        message: `${who} subscription is suspended. Scheduled scans are paused until it is reactivated.`,
      };
    case 'subscription_canceled':
      return {
        title: 'Subscription canceled — scans paused',
        message: `${who} subscription has been canceled. Scheduled scans are paused until it is reactivated.`,
      };
    default:
      return { title: '', message: '' };
  }
}

// useClientSubscriptions fetches all client subscriptions (admin/superuser only) and returns a
// lookup keyed by client_id. Non-fatal: if the fetch fails, the map stays empty (no banners).
export function useClientSubscriptions(enabled: boolean): {
  byClient: Record<number, ClientSubscription>;
  loading: boolean;
  reload: () => void;
} {
  const [byClient, setByClient] = useState<Record<number, ClientSubscription>>({});
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    if (!enabled) {
      setByClient({});
      return;
    }
    setLoading(true);
    subscriptionAPI
      .adminGetAll()
      .then((res) => {
        const m: Record<number, ClientSubscription> = {};
        (res.data || []).forEach((s) => {
          m[s.client_id] = s;
        });
        setByClient(m);
      })
      .catch(() => {
        // Non-fatal: without data we simply show no subscription banners.
        setByClient({});
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { byClient, loading, reload };
}
